import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import FloatingNav from "./components/FloatingNav";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { SocialMediaWidget } from "./components/SocialMediaWidget";
import usePageTracking, { initGA } from "./hooks/useAnalytics";
import AuthCallback from "./components/AuthCallback";

// Pages
import HomePage from "./pages/HomePage";
import BrowsePage from "./pages/BrowsePage";
import ComicDetailPage from "./pages/ComicDetailPage";
import ReaderPage from "./pages/ReaderPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import DashboardPage from "./pages/DashboardPage";
import PricingPage from "./pages/PricingPage";
import DonationSetupPage from "./pages/DonationSetupPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import CreatorPage from "./pages/CreatorPage";
import CreatorsListPage from "./pages/CreatorsListPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import CreatePage from "./pages/CreatePage";
import AdminPage from "./pages/AdminPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AIToolsPage from "./pages/AIToolsPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import FeedbackPage from "./pages/FeedbackPage";
import PrizesPage from "./pages/PrizesPage";
import CreatorAnalyticsPage from "./pages/CreatorAnalyticsPage";
import ProfilePage from "./pages/ProfilePage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CustomerServicePage from "./pages/CustomerServicePage";
import CreatorApplicationPage from "./pages/CreatorApplicationPage";
import CollectionsPage from "./pages/CollectionsPage";
import CollectionDetailPage from "./pages/CollectionDetailPage";
import ComicCreatorStudio from "./pages/ComicCreatorStudio";
import AuthCallbackPage from "./pages/AuthCallbackPage";

// Initialize Google Analytics on app load
initGA();

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Router wrapper to detect session_id in URL hash (for Google OAuth callback)
function AppRouter() {
  const location = useLocation();
  
  // Check URL hash for session_id synchronously (before ProtectedRoute runs)
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return <AppRoutes />;
}

function ProtectedRoute({ children, requireAuth = true, requireAdmin = false, requireCreator = false }) {
  const { isAuthenticated, isAdmin, isCreator, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-hot-rod border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  if (requireCreator && !isCreator) {
    return <Navigate to="/pricing" replace />;
  }
  
  return children;
}

function Layout({ children, showNav = true, showHeader = true, showFooter = true, showSocial = true }) {
  return (
    <>
      {showHeader && <Header />}
      {children}
      {showFooter && <Footer />}
      {showNav && <FloatingNav />}
      {showSocial && <SocialMediaWidget />}
      <div className="noise-overlay" />
    </>
  );
}

// Analytics wrapper component
function AnalyticsWrapper({ children }) {
  usePageTracking();
  return children;
}

function AppRoutes() {
  return (
    <AnalyticsWrapper>
    <Routes>
      {/* Auth Callback Route - for popup-based OAuth */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      
      {/* Public Routes */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/browse" element={<Layout><BrowsePage /></Layout>} />
      <Route path="/comic/:comicId" element={<Layout><ComicDetailPage /></Layout>} />
      <Route path="/read/:comicId" element={<Layout showNav={false} showHeader={false} showFooter={false} showSocial={false}><ReaderPage /></Layout>} />
      <Route path="/read/:comicId/:episodeId" element={<Layout showNav={false} showHeader={false} showFooter={false} showSocial={false}><ReaderPage /></Layout>} />
      <Route path="/creators" element={<Layout><CreatorsListPage /></Layout>} />
      <Route path="/creator/:creatorId" element={<Layout><CreatorPage /></Layout>} />
      <Route path="/leaderboard" element={<Layout><LeaderboardPage /></Layout>} />
      <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
      <Route path="/about" element={<Layout><AboutPage /></Layout>} />
      <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
      <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
      <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
      <Route path="/blog/:postId" element={<Layout><BlogPostPage /></Layout>} />
      <Route path="/prizes" element={<Layout><PrizesPage /></Layout>} />
      <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
      <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
      <Route path="/customer-service" element={<Layout><CustomerServicePage /></Layout>} />
      <Route path="/collections" element={<Layout><CollectionsPage /></Layout>} />
      <Route path="/collections/:id" element={<Layout><CollectionDetailPage /></Layout>} />
      <Route path="/creator-application" element={
        <ProtectedRoute>
          <Layout><CreatorApplicationPage /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Auth Routes */}
      <Route path="/login" element={<Layout showNav={false} showHeader={false} showFooter={false} showSocial={false}><LoginPage /></Layout>} />
      <Route path="/signup" element={<Layout showNav={false} showHeader={false} showFooter={false} showSocial={false}><SignupPage /></Layout>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><DashboardPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/donations" element={
        <ProtectedRoute>
          <Layout><DonationSetupPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/create" element={
        <ProtectedRoute requireCreator>
          <Layout><CreatePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/ai-tools" element={
        <ProtectedRoute requireCreator>
          <Layout><AIToolsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/creator-studio" element={
        <ProtectedRoute requireCreator>
          <Layout><ComicCreatorStudio /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/feedback" element={
        <ProtectedRoute>
          <Layout><FeedbackPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute requireCreator>
          <Layout><CreatorAnalyticsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout><ProfilePage /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Payment Routes */}
      <Route path="/subscription/success" element={<Layout><PaymentSuccessPage /></Layout>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <Layout><AdminPage /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </AnalyticsWrapper>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
